import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import type { ReactNode } from 'react';
import { formatINR, statusTone } from '@pmc/shared';
import { PMC, tint } from '../theme';

/**
 * Small shared building blocks. Kept together because each is a handful of
 * lines and they are almost always imported as a group.
 */

/** Bill / transaction / application status, coloured by meaning. */
export function StatusChip({ status }: { status: string }) {
  const tone = statusTone(status);
  const colours = {
    success: { fg: PMC.success, bg: PMC.successBg },
    warning: { fg: PMC.warning, bg: PMC.warningBg },
    error: { fg: PMC.danger, bg: PMC.dangerBg },
    info: { fg: PMC.primary, bg: PMC.primaryLight },
  }[tone];

  return (
    <Chip
      label={status}
      size="small"
      sx={{ color: colours.fg, backgroundColor: colours.bg, border: 'none' }}
    />
  );
}

/** Rupee amount with tabular figures so columns align. */
export function Money({
  value,
  variant = 'body2',
  bold = false,
  color,
}: {
  value: number;
  variant?: 'body1' | 'body2' | 'h3' | 'h4' | 'caption';
  bold?: boolean;
  color?: string;
}) {
  return (
    <Typography
      component="span"
      variant={variant}
      className="num"
      sx={{ fontWeight: bold ? 700 : 500, color, whiteSpace: 'nowrap' }}
    >
      {formatINR(value)}
    </Typography>
  );
}

/** Headline figure tile for dashboards. */
export function StatCard({
  label,
  value,
  hint,
  tone = PMC.primary,
  icon,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: string;
  icon?: ReactNode;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {icon ? (
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                backgroundColor: tint(tone, 0.1),
                color: tone,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0, width: '100%' }}>
            <Tooltip title={label}>
              <Typography variant="subtitle2" noWrap sx={{ mb: 0.25 }}>
                {label}
              </Typography>
            </Tooltip>
            <Tooltip title={typeof value === 'string' ? value : ''}>
              <Typography
                variant="h4"
                className="num"
                noWrap
                sx={{ color: tone, lineHeight: 1.2 }}
              >
                {value}
              </Typography>
            </Tooltip>
            {hint ? (
              <Tooltip title={hint}>
                <Typography variant="caption" color="text.secondary" noWrap component="p">
                  {hint}
                </Typography>
              </Tooltip>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Text search box with a clear (×) button that appears once there's something to clear. */
export function SearchField({
  value,
  onChange,
  label = 'Search',
  placeholder,
  sx,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
}) {
  return (
    <TextField
      size="small"
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={sx}
      slotProps={{
        input: {
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton size="small" edge="end" aria-label="Clear search" onClick={() => onChange('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      }}
    />
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={1.5}
      sx={{ mb: 2.5 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h2" sx={{ mb: subtitle ? 0.5 : 0 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" component="div">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
  );
}

/** Card with a title bar, used for every content block. */
export function SectionCard({
  title,
  icon,
  accent,
  action,
  children,
  dense = false,
}: {
  title?: string;
  icon?: ReactNode;
  /** Tints the title band and icon badge — pass a different colour per card to tell sections apart at a glance. Omit for the plain white band used elsewhere. */
  accent?: string;
  action?: ReactNode;
  children: ReactNode;
  dense?: boolean;
}) {
  const iconColor = accent ?? PMC.primary;

  return (
    <Card>
      {title ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.25,
            py: 1.5,
            borderBottom: `1px solid ${PMC.line}`,
            backgroundColor: accent ? tint(accent, 0.16) : undefined,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            {icon ? (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: tint(iconColor, 0.28),
                  color: iconColor,
                  flexShrink: 0,
                  '& svg': { fontSize: 16 },
                }}
              >
                {icon}
              </Box>
            ) : null}
            <Typography variant="h5">{title}</Typography>
          </Stack>
          {action}
        </Stack>
      ) : null}
      <Box sx={{ p: dense ? 0 : 2.25 }}>{children}</Box>
    </Card>
  );
}

export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 5, px: 2, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
      {action}
    </Stack>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <Alert severity="error" sx={{ my: 2 }}>
      {message}
    </Alert>
  );
}

export function LoadingBlock({ height = 180 }: { height?: number }) {
  return <Skeleton variant="rounded" height={height} sx={{ borderRadius: 2 }} />;
}

/** Full-page spinner for route-level suspense. */
export function LoadingPage() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '50vh' }}>
      <CircularProgress />
    </Stack>
  );
}

/** Table-shaped skeleton, so a loading list does not collapse the layout. */
export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <Box sx={{ p: 2 }}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Stack key={rowIndex} direction="row" spacing={2} sx={{ mb: 1.25 }}>
          {Array.from({ length: columns }).map((__, colIndex) => (
            <Skeleton key={colIndex} variant="text" sx={{ flex: colIndex === 0 ? 1.6 : 1 }} />
          ))}
        </Stack>
      ))}
    </Box>
  );
}

/** Label/value row used across bill, receipt and application summaries. */
export function KeyValue({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: ReactNode;
  strong?: boolean;
}) {
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={2}
      sx={{ py: 0.65, borderBottom: `1px dashed ${PMC.line}` }}
    >
      <Typography variant="body2" color="text.secondary" component="div" sx={{ width: 220, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ fontWeight: strong ? 700 : 500, textAlign: 'left', wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
